import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";
import { ExportProvider, useExport } from "@/context/ExportContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Ticket, Users, BookOpen, BarChart3, Settings,
  Crown, LogOut, Plus, Download, ChevronRight, Shield, UserCog, Menu,
  Contact2,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useEffect, useMemo, useState } from "react";
import NewTicketDialog from "@/components/NewTicketDialog";
import TicketTypeChooser from "@/components/TicketTypeChooser";
import NotificationBell from "@/components/NotificationBell";
import GlobalSearch from "@/components/GlobalSearch";
import ClientSwitcher from "@/components/ClientSwitcher";
import { TicketCreatorProvider } from "@/context/TicketCreatorContext";
import { PrioritiesProvider } from "@/context/PrioritiesContext";
import useGenesysScreenPop from "@/hooks/useGenesysScreenPop";

// Permissions used by any Settings tab — sidebar hides "Settings" if user has none.
// MUST stay the union of every tab's perms (ticketing Settings + LeadSettings):
// visibility is computed from effective permissions, never from role name.
const SETTINGS_PERMS = [
  // ticketing settings tabs
  "users.create", "users.edit", "users.manage_permissions",
  "catalogue.manage_issue_types", "catalogue.manage_departments",
  "automation.edit", "logs.view_change", "email_templates.edit",
  "field_rules.edit", "sla.edit",
  // lead settings tabs
  "users.view", "catalogue.view_lookups", "email_templates.view",
  "leads.manage_dropdowns", "leads.manage_fields",
  "leads.import", "leads.view_imports",
  "leads.import_call_logs", "leads.view_call_log_imports",
  "leads.manage_dialer", "leads.manage_schedules", "leads.manage_system_config",
  "leads.view_assigning_logic", "leads.manage_assigning_logic",
];

const TICKET_VIEW_PERMS = [
  "tickets.view_own", "tickets.view_team",
  "tickets.view_department", "tickets.view_all",
];

// Nav is now module-aware. `module` maps to the client's `modules` array.
// If a nav entry doesn't declare a module, it renders regardless (unusual — every
// module-scoped feature should declare its key).
const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", module: "dashboard", requiresAnyPerm: ["module.dashboards", "dashboard.view"] },
  { to: "/tickets", icon: Ticket, label: "Tickets", module: "tickets", requiresAnyPerm: ["module.tickets", ...TICKET_VIEW_PERMS] },
  { to: "/leads", icon: Contact2, label: "Leads", module: "leads" },
  { to: "/customers", icon: Users, label: "Customers", module: "customers", requiresAnyPerm: ["module.customers", "customers.view_details"] },
  { to: "/pocs", icon: UserCog, label: "POCs", module: "pocs", requiresAnyPerm: ["contacts.view"] },
  { to: "/knowledge-base", icon: BookOpen, label: "Knowledge Base", module: "knowledge_base", requiresAnyPerm: ["module.knowledge_base", "kb.view"] },
  { to: "/reports", icon: BarChart3, label: "Reports", module: "reports", requiresAnyPerm: ["module.reports", "reports.view"] },
  { to: "/settings", icon: Settings, label: "Settings", module: "settings", requiresAnyPerm: ["module.settings", ...SETTINGS_PERMS] },
];

const TITLES = {
  "/dashboard": "Dashboard",
  "/tickets": "Tickets",
  "/leads": "Leads",
  "/customers": "Customers",
  "/contacts": "Customers",
  "/pocs": "Department POCs",
  "/knowledge-base": "Knowledge Base",
  "/reports": "Reports",
  "/settings": "Settings",
};

function SidebarInner({ visibleNav, onNavigate }) {
  return (
    <>
      <div className="px-6 py-6 border-b border-slate-800">
        <Link to="/dashboard" className="block" data-testid="brand-link" onClick={onNavigate}>
          <div className="inline-flex items-end gap-3">
            <img
              src="/brand/kippu-logo.svg"
              alt="Kippu"
              className="h-14 w-auto select-none rounded-md"
              draggable={false}
            />
            <div className="flex flex-col leading-none pb-1.5">
              <span className="text-[9px] uppercase tracking-[0.24em] text-slate-400">BY</span>
              <span className="text-[11px] uppercase tracking-[0.24em] text-slate-200 font-medium mt-1">ZEFFICACY</span>
            </div>
          </div>
        </Link>
      </div>
      <nav className="flex-1 py-4 overflow-y-auto">
        {visibleNav.map((item) => (
          <NavLink
            key={item.to} to={item.to}
            onClick={onNavigate}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-6 py-3 text-sm transition-colors hover:bg-slate-800/60 ${
                isActive ? "nav-active text-white font-medium" : "text-slate-300"
              }`
            }
          >
            <item.icon className="w-4 h-4" strokeWidth={1.75} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-slate-800 text-xs text-slate-500">
        <div className="flex items-center gap-1.5">
          <Shield className="w-3 h-3" /><span>v1.1 · Omnichannel</span>
        </div>
      </div>
    </>
  );
}

function TopbarInner({ user, navigate, location, setOpenNewTicket, setMobileOpen, clientKind }) {
  const { trigger: triggerExport, hasHandler } = useExport();
  const { hasPerm } = useAuth();
  const isTicketing = clientKind === "ticketing";
  const canCreateTicket = isTicketing && hasPerm("tickets.create");
  const canExport = hasPerm("reports.export_excel") || hasPerm("dashboard.export_data");
  const baseKey = "/" + (location.pathname.split("/")[1] || "dashboard");
  const title = TITLES[baseKey] || "Kippu";
  const initials = (user?.name || "?")
    .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <header
      data-testid="topbar"
      className="h-16 bg-white border-b border-slate-200 sticky top-0 z-30 flex items-center justify-between px-4 md:px-8 gap-2"
    >
      <div className="flex items-center gap-3 min-w-0">
        {/* Hamburger — visible only below md */}
        <Button
          variant="ghost" size="icon"
          className="md:hidden text-slate-700 -ml-1"
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-nav-trigger"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </Button>
        <div className="min-w-0">
          <h1 className="font-heading text-lg font-semibold text-slate-900 truncate" data-testid="page-title">{title}</h1>
          <div className="hidden sm:flex items-center gap-1 text-xs text-slate-500 mt-0.5">
            <span>Kippu</span><ChevronRight className="w-3 h-3" /><span className="text-slate-700">{title}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 md:gap-3">
        <ClientSwitcher />
        <div className="hidden md:block"><GlobalSearch /></div>

        {canExport && (
          <Button
            variant="outline" size="sm" data-testid="export-csv-btn"
            onClick={triggerExport}
            disabled={!hasHandler}
            className="border-slate-200 hidden sm:inline-flex"
            title={hasHandler ? "Export current page to CSV" : "Nothing to export on this page"}
          >
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        )}

        <NotificationBell />

        {canCreateTicket && (
          <Button
            size="sm" data-testid="new-ticket-btn"
            onClick={() => setOpenNewTicket(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Plus className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">New Ticket</span>
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger data-testid="user-menu" className="flex items-center gap-2 pl-1 outline-none">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
            {user?.is_super_admin && <Crown className="w-3.5 h-3.5 text-amber-500" data-testid="super-admin-crown" />}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel>
              <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                {user?.name}
                {user?.is_super_admin && <Crown className="w-3.5 h-3.5 text-amber-500" />}
              </div>
              <div className="text-xs text-slate-500 font-normal mt-0.5">{user?.email}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">
                {user?.is_super_admin ? "Super Admin · God Mode" : user?.role}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="logout-btn"
              onClick={() => { user && navigate("/login"); localStorage.clear(); window.location.href = "/login"; }}
              className="text-rose-600 focus:text-rose-700"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export default function AppShell() {
  const { user, hasPerm } = useAuth();
  const { active: activeClient, hasModule } = useClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [openNewTicket, setOpenNewTicket] = useState(false);
  const [openTypeChooser, setOpenTypeChooser] = useState(false);
  const [pickedTicketType, setPickedTicketType] = useState(null);
  const [prefillCustomerId, setPrefillCustomerId] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Real-time Genesys screen-pop listener (no-op when SSE is unavailable).
  useGenesysScreenPop();

  // Close drawer whenever route changes (mobile UX).
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Filter nav by (a) the active client's enabled modules and (b) the user's
  // permission grants. If a module isn't enabled for the tenant, the sidebar
  // entry vanishes entirely (Ooredoo hides Tickets/Customers/POCs).
  const visibleNav = useMemo(
    () => NAV.filter((item) => {
      if (item.module && !hasModule(item.module)) return false;
      if (!item.requiresAnyPerm) return true;
      return item.requiresAnyPerm.some((p) => hasPerm(p));
    }),
    [hasPerm, hasModule]
  );

  // Guard: if the user landed on a page whose module is disabled for the
  // active client, bounce them to /dashboard. Prevents ghost Tickets from
  // Starlink appearing when switching to Ooredoo without reloading.
  useEffect(() => {
    const seg = "/" + (location.pathname.split("/")[1] || "");
    const entry = NAV.find((n) => n.to === seg);
    if (entry?.module && !hasModule(entry.module)) {
      navigate("/dashboard", { replace: true });
    }
  }, [location.pathname, hasModule, navigate]);

  return (
    <ExportProvider>
      <PrioritiesProvider>
      <TicketCreatorProvider onOpen={({ customerId }) => {
        setPrefillCustomerId(customerId);
        setOpenTypeChooser(true);
      }}>
      <div className="min-h-screen bg-slate-50">
        {/* Desktop sidebar (≥ md) */}
        <aside
          data-testid="sidebar"
          className="hidden md:flex fixed left-0 top-0 h-full w-52 bg-slate-900 text-slate-100 z-40 flex-col"
        >
          <SidebarInner visibleNav={visibleNav} />
        </aside>

        {/* Mobile drawer (< md) */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent
            side="left"
            className="md:hidden w-64 p-0 bg-slate-900 text-slate-100 border-r-0 [&>button]:text-slate-100"
            data-testid="mobile-sidebar"
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Navigation menu</SheetTitle>
            </SheetHeader>
            <div className="flex flex-col h-full">
              <SidebarInner visibleNav={visibleNav} onNavigate={() => setMobileOpen(false)} />
            </div>
          </SheetContent>
        </Sheet>

        <div className="md:ml-52 flex flex-col min-h-screen">
          <TopbarInner
            user={user} navigate={navigate} location={location}
            setOpenNewTicket={() => setOpenTypeChooser(true)}
            setMobileOpen={setMobileOpen}
            clientKind={activeClient?.kind}
          />
          <main className="p-4 md:p-8 flex-1"><Outlet /></main>
        </div>

        <TicketTypeChooser
          open={openTypeChooser}
          onOpenChange={(v) => { setOpenTypeChooser(v); if (!v && !openNewTicket) setPrefillCustomerId(null); }}
          onPicked={(label) => { setPickedTicketType(label); setOpenTypeChooser(false); setOpenNewTicket(true); }}
        />
        <NewTicketDialog
          open={openNewTicket}
          onOpenChange={(v) => { setOpenNewTicket(v); if (!v) { setPickedTicketType(null); setPrefillCustomerId(null); } }}
          initialType={pickedTicketType}
          prefillCustomerId={prefillCustomerId}
        />
      </div>
      </TicketCreatorProvider>
      </PrioritiesProvider>
    </ExportProvider>
  );
}
